import { Model as VegasModel } from '@mode-vegas/vegas';
import { Map, OrderedSet } from 'immutable';

import { Flamingo } from '@mode/charts/util-vegas';
import { ChartActions, ChartSwitchActions, TempActions } from '@mode/charts/domain';
import {
  QueryResource,
  QueryResultResource,
  QueryRunResource,
  QueryRunResourceHelpers,
  ReportRunResource,
  TableResource,
} from '@mode/legacy/data-access-model-js';
import { ReportResourceHelper } from '@mode/shared/util-halogen';
import {
  AppState,
  ImmutableRecordSet,
  ModelState,
  QueryRunState,
  QueryState,
  ReportRunState,
  SelectedReportState,
  TableState,
} from '@mode/legacy/types-app-state';
import { NotebookActionType, ReducerTree } from '@mode/legacy/util-domain';
import { MetricsActions } from '@mode/metrics/domain';
import { FlamingoTypes, RUNNING_QUERY_STATES, ReportTypes } from '@mode/shared/contract-common';
import { annotateSqlWithSyntaxError, getFormattedDuration, immRemoveKey } from '@mode/shared/util-js';
import { Switch } from '@mode/shared/util-switch';
import { FlamingoActions, SwitchAttrsActions, SwitchViewActions } from '@mode/switch-views/contract';
import { ChartDesignerActionType } from '../../actions/chart-designer/designer.actions';
import { ChartModelActionType } from '../../actions/chart.actions';
import { ClientActionType } from '../../actions/client.actions';
import { EditorActionType } from '../../actions/editor-actions';
import { Actions } from '../../actions/index';
import { CellActions } from '../../actions/notebooks/cell.actions';
import { QueryActionType } from '../../actions/query.actions';
import { ReportActionType } from '../../actions/report-actions';
import { ReportLayoutActions } from '../../actions/report-layout.actions';
import { ReportRunActionType } from '../../actions/report-run.actions';
import { RouterActionType } from '../../actions/router-actions';
import { VizTypes } from '../../common/types';
import { generatePreviewTableQuery } from '../../lib/click-to-preview-helpers';
import { ReportLayoutHelpers } from '../../lib/report-layout-helpers';
import { initialNormalizedNotebookViz, normalizedNotebookVizReducer } from './selected-report/notebook-viz.reducers';

function isRunning(run: QueryRunResource) {
  return RUNNING_QUERY_STATES.indexOf(run.state) >= 0;
}

function updateReportRunLinks(state, link) {
  (state.resource as any)._links.share.href = link;
  (state.resource as any)._links.clone.href = `${link}/clone`;
  (state.resource as any)._links.web_clone.href = `${link}/clone`;
  (state.resource as any)._links.content.href = `/api${link}/results/content.csv`;
  (state.resource as any)._links.embed.href = `${link}/embed`;
  (state.resource as any)._links.self.href = `/api${link}?embed[result]=1`;
  (state.resource as any)._links.pdf_export.href = `/api${link}/pdf.pdf`;
  (state.resource as any)._links.preview.href = `/api${link}/results/preview`;
  (state.resource as any)._links.python_cell_runs.href = `/api${link}/notebook_cell_runs`;

  return { ...state };
}

export function queryResourceToState(query: QueryResource): QueryState {
  return {
    name: query.name,
    token: query.token,
    resource: query,
    raw_query: query.raw_query,
    data_source_id: query.data_source_id,
    limit: query.limit,
    explorations_count: query.explorations_count,
    report_imports_count: query.report_imports_count,
    last_run_id: query.last_run_id,
    formatted: query.formatted,
    dbt_metric_token: query.dbt_metric_token,
    dbt_metric_id: query.dbt_metric_id,
    charts: query.charts,
  };
}

export function reportRunResourceToState(reportRun: ReportRunResource): ReportRunState {
  return {
    resource: reportRun,
    state: reportRun.state,
    running: reportRun.state === 'running' || reportRun.state === 'pending',
    loading: false,
    canceling: false,
    share: reportRun.share,
    token: reportRun.token,
    createdAt: new Date(reportRun.created_at),
  };
}

export function tableResourceToState(queryToken: string, table: TableResource): TableState {
  return {
    vizType: VizTypes.Table,
    resource: table,
    currentView: table.view,
    history: [],
    token: table.token,
    queryToken: queryToken,
  };
}

export function queryRunResourceToState(
  queryRun: QueryRunResource,
  cancel: boolean,
  run: boolean,
  runBytes: number | undefined,
  contentLength: number | undefined
): QueryRunState {
  const end = new Date(queryRun.completed_at);
  const start = new Date(queryRun.startedAt || queryRun.created_at);
  const elapsed = Math.max(0, end.getTime() - start.getTime());

  const oversizeResult = QueryRunResourceHelpers.parseOversizeErrorMessage(queryRun);

  return {
    resource: queryRun,
    token: queryRun.token,
    datasourceToken: queryRun.data_source_token,
    state: queryRun.state,
    query_token: queryRun.query_token,
    limit: queryRun.limit,
    bytes: runBytes,
    content_length: contentLength,
    running: run || isRunning(queryRun),
    warning: cancel || queryRun.state === 'failed' || queryRun.state === 'cancelled',
    runQueued: run,
    cancelQueued: cancel,
    timeCompleted: getFormattedDuration(elapsed),
    reportDataUrl: queryRun.hasLink('result_web') ? queryRun.links('result_web').href : '',
    reportQueryUrl: queryRun.hasLink('query_web') ? queryRun.links('result_web').href : '',
    reportRunUrl: queryRun.hasLink('report_run_web') ? queryRun.links('result_web').href : '',
    renderedSource: queryRun.rendered_source,
    rawSource: queryRun.raw_source,
    createdAt: queryRun.created_at,

    errorLineHtml: annotateSqlWithSyntaxError({
      state: queryRun.state,
      errorMessage: queryRun.error_message,
      renderedSource: queryRun.rendered_source,
    }),
    errorCode: queryRun.error_code,
    errorMessage: queryRun.error_message,
    ...oversizeResult, // keeping it flat for easier compatibility in existing usage
  };
}

export function queryResultToState(
  query: QueryResource,
  queryResult: QueryResultResource,
  queryRun: QueryRunResource,
  model: VegasModel,
  content: any[]
): ModelState {
  const oversized = queryResult.content_length >= 15728640;
  return {
    token: queryRun.query_token,
    model: model,
    state: queryRun.state,
    count: queryResult.count,
    bytes: queryResult.bytes,
    content: content,
    columns: queryResult.columns,
    underBrowserSizeLimit: !oversized,
    csv: queryResult.hasLink('csv') ? queryResult.links('csv').href : undefined,
    json: queryResult.hasLink('json') ? queryResult.links('json').href : undefined,
    oversized: oversized,
    query_token: query.token,

    queryName: query.name,
    reportDataUrl: queryRun.hasLink('result_web') ? queryRun.links('result_web').href : undefined,
    reportQueryUrl: queryRun.hasLink('query_web') ? queryRun.links('query_web').href : undefined,
    content_length: queryResult.content_length,
  };
}

function makeErrorQueryResult(query: QueryResource, queryRun: QueryRunResource): ModelState {
  return {
    token: queryRun.query_token,
    model: VegasModel.build([], []),
    state: queryRun.state,
    error_message: queryRun.error_message,
    count: 0,
    bytes: 0,
    content: [],
    columns: [],
    underBrowserSizeLimit: false,
    csv: undefined,
    json: undefined,
    oversized: false,

    queryName: query.name,
    reportDataUrl: queryRun.hasLink('result_web') ? queryRun.links('result_web').href : undefined,
    reportQueryUrl: queryRun.hasLink('query_web') ? queryRun.links('query_web').href : undefined,
    content_length: 0,
  };
}

export const initialSelectedReportState: SelectedReportState = {
  model: Switch.Models.empty(),
  chartModels: new ImmutableRecordSet<ModelState>('token'),
  chartsByQuery: Map<string, OrderedSet<string>>(),
  columnsByQuery: Map<string, FlamingoTypes.RecordsResult['columns']>(),
  creator: null,
  queries: new ImmutableRecordSet<QueryState>('token'),
  queryRuns: new ImmutableRecordSet<QueryRunState>('query_token'),
  queryViewResourceByQuery: {},
  report: null,
  theme: null,
  reportIsNew: null,
  reportDisplayName: null,
  reportLayout: '',
  renderedReportLayout: '',
  reportRun: null,
  reportRunPdfExport: null,
  reportSpace: null,
  switchAttrsByQuery: {},
  tables: new ImmutableRecordSet<TableState>('token'),
  tablesByQuery: Map<string, OrderedSet<string>>(),
  userStar: null,
  pollingReportRun: false,
  calcEditorModal: {
    isOpen: false,
    viewName: null,
  },
  notebookViz: initialNormalizedNotebookViz,
  hasRecentReportRun: false,
  recentReportRun: null,
};

// Client State
function creatorReducer(state = initialSelectedReportState.creator, action: Actions): SelectedReportState['creator'] {
  switch (action.type) {
    case ClientActionType.LoadReportCreatorSuccess:
      return action.creatorResource;
  }
  return state;
}

function queriesReducer(state = initialSelectedReportState.queries, action: Actions): SelectedReportState['queries'] {
  switch (action.type) {
    case RouterActionType.UpdateRoute:
      if (!action.payload.params.reportId) {
        return state.clear();
      }
      break;
    case QueryActionType.UpdateQuerySuccess:
      return state.mergeRecord(queryResourceToState(action.payload));
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeRecordByKey(action.token);
    case QueryActionType.BatchUpdateQueries:
      // Remap query tokens, and update query information, but not transient raw_query

      // Find "new" queries that will need to be removed
      const remappedQueries = action.payload.filter((q) => q.mapping_id);
      const otherQueries = action.payload.filter((q) => !q.mapping_id);

      // Merge in remapped queries, leaving transient raw_query intact
      state = state.mergeRecords(
        remappedQueries.map((q) => {
          return {
            ...queryResourceToState(q),
            raw_query: state.getRecord(q.mapping_id)?.raw_query ?? q.raw_query,
            dbt_metric_token: state.getRecord(q.mapping_id)?.dbt_metric_token ?? q.dbt_metric_token,
            charts: state.getRecord(q.mapping_id)?.charts ?? q.charts,
            dbt_metric_id: state.getRecord(q.mapping_id)?.dbt_metric_id ?? q.dbt_metric_id,
          };
        })
      );

      // Merge in other queries, leaving transient raw_query intact
      state = state.mergeRecords(
        otherQueries.map((q) => {
          return {
            ...queryResourceToState(q),
            raw_query: state.getRecord(q.token)?.raw_query ?? q.raw_query,
            dbt_metric_token: state.getRecord(q.token)?.dbt_metric_token ?? q.dbt_metric_token,
            charts: state.getRecord(q.token)?.charts ?? q.charts,
            dbt_metric_id: state.getRecord(q.mapping_id)?.dbt_metric_id ?? q.dbt_metric_id,
          };
        })
      );

      return state.removeRecordsByKey(remappedQueries.map((q) => q.mapping_id));
    case QueryActionType.SaveQuerySuccess:
      return state.mergeRecord(queryResourceToState(action.payload.query)).removeRecordByKey(action.payload.oldToken);
    case QueryActionType.CreateQuerySuccess:
      return state.mergeRecord(queryResourceToState(action.payload));
    case MetricsActions.createMetricQuerySuccess.type:
      return state.mergeRecord(queryResourceToState(action.query));
    case QueryActionType.LoadQueriesSuccess:
      return state.replaceRecords(action.queries.map((q) => queryResourceToState(q)));
    case QueryActionType.RefreshQueriesAfterCellExport:
      const queries = action.queries.filter((q) => !state.hasRecord(q.token)); // will contain exactly one query resource
      return state.mergeRecords(queries.map((q) => queryResourceToState(q)));
    case ReportActionType.LoadEmbeddedReport: // fallthrough
    case ClientActionType.LoadReportSuccess:
      return state.clear();
    case QueryActionType.FormatQuerySuccess:
      return state.mergeRecord({
        ...state.getRecord(action.payload.query.token),
        resource: action.payload.query,
        raw_query: action.payload.raw_query,
        formatted: action.payload.formatted,
        original_query: action.payload.original_query,
        formatted_query: action.payload.formatted_query,
      });
    case QueryActionType.UpdateQueryText:
      return state.mergeRecord({
        ...state.getRecord(action.payload.queryToken),
        raw_query: action.payload.queryText,
      });
    case EditorActionType.UpdateQueryEditor:
      return state.mergeRecord({
        ...state.getRecord(action.payload.selectedQueryToken),
        raw_query: action.payload.text,
      });
    case EditorActionType.InsertQueryText: {
      const current = state.getRecord(action.payload.selectedQueryToken);
      const text = current?.raw_query || '';
      return state.mergeRecord({
        ...current,
        raw_query:
          text.slice(0, action.payload.selectionStart) +
          action.payload.text +
          text.slice(action.payload.selectionStart),
      });
    }
    case EditorActionType.RunPreviewTableQuery: {
      const current = state.getRecord(action.payload.selectedQueryToken);
      const previewTableQuery = generatePreviewTableQuery(action.payload);
      return state.mergeRecord({
        ...current,
        raw_query: current.raw_query.trim() ? previewTableQuery + '\n\n' + current.raw_query : previewTableQuery,
      });
    }
    case QueryActionType.UpdateQueryName:
      return state.mergeRecord({
        ...state.getRecord(action.payload.queryToken),
        name: action.payload.queryName,
      });
    case QueryActionType.UpdateQueryLimit:
      // This is fired on any poll report run, which can be on embeds, which won't load queries
      if (state.hasRecord(action.payload.queryToken)) {
        return state.mergeRecord({
          ...state.getRecord(action.payload.queryToken),
          limit: action.payload.queryLimit,
        });
      }
      break;
    case QueryActionType.UpdateQueryDatasource:
      return state.mergeRecord({
        ...state.getRecord(action.payload.queryToken),
        data_source_id: action.payload.queryDatasource,
      });
    case QueryActionType.UpdateQueryEncodingSuccess: {
      return state.mergeRecord({
        ...state.getRecord(action.payload.queryToken),
        raw_query: action.payload.raw_query,
      });
    }
  }

  return state;
}

function removeVizToken(byQuery: Map<string, OrderedSet<string>>, token: string): Map<string, OrderedSet<string>> {
  return byQuery.withMutations((queries) => {
    const entry = queries.findEntry((q) => q.has(token));
    if (entry) {
      queries.set(
        entry[0],
        entry[1].filter((c) => c !== token)
      );
    }
  });
}

function chartsByQueryReducer(
  state = initialSelectedReportState.chartsByQuery,
  action: Actions
): SelectedReportState['chartsByQuery'] {
  switch (action.type) {
    case RouterActionType.UpdateRoute:
      if (!action.payload.params.reportId) {
        return state.clear();
      }
      break;
    case ReportActionType.LoadEmbeddedReport: // fallthrough
    case ClientActionType.LoadReportSuccess:
      return state.clear();
    // @todo: ChartSwitchActions.createNewChartSuccess.type is temporary - adds the chart
    // to the state as a result of creating a viz for a metric. the metric route doesn't
    // have viz routes/tabs so we need to select the viz token based on the selected metric
    // query's token. This can be removed once the api accepts a chart resource as part of the run
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartActions.createChartSuccess.type: {
      const chart = action.type === ChartSwitchActions.createNewChartSuccess.type ? action.chart : action.payload;
      if (!state.has(action.queryToken)) {
        return state.set(action.queryToken, OrderedSet([chart.token]));
      } else {
        return state.update(action.queryToken, (set) => set.add(chart.token));
      }
    }
    case ChartActions.loadChartsSuccess.type:
      return state.set(action.queryToken, OrderedSet(action.payload.map((chart) => chart.token)));
    case ChartActions.destroyChart.type:
      return removeVizToken(state, action.payload);
    case ChartActions.destroyChartFailure.type:
      return state.update(action.queryToken, (set) => set.add(action.token));
    case TempActions.Type.DestroyQuerySuccess:
      return state.remove(action.token);
    case QueryActionType.BatchUpdateQueries: {
      const updatedQuery = action.payload.find((q) => q.mapping_id != null);
      if (updatedQuery) {
        return state.set(updatedQuery.token, OrderedSet());
      }
      break;
    }
  }
  return state;
}

function tablesByQueryReducer(
  state = initialSelectedReportState.tablesByQuery,
  action: Actions
): SelectedReportState['tablesByQuery'] {
  switch (action.type) {
    case RouterActionType.UpdateRoute:
      if (!action.payload.params.reportId) {
        return state.clear();
      }
      break;
    case ReportActionType.LoadEmbeddedReport: // fallthrough
    case ClientActionType.LoadReportSuccess:
      return state.clear();
    case ChartModelActionType.LoadEmbeddedTablesSuccess:
      return state.set(action.queryToken, OrderedSet(action.payload.map((table) => table.token)));
    case ChartModelActionType.AddTableSuccess:
      return state.set(action.queryToken, state.get(action.queryToken).add(action.payload.token));
    case ChartModelActionType.DestroyTable:
      return state.remove(action.queryToken);
    case QueryActionType.BatchUpdateQueries:
      const updatedQuery = action.payload.find((q) => q.mapping_id != null);
      if (updatedQuery) {
        return state.set(updatedQuery.token, OrderedSet());
      }
      break;
    case TempActions.Type.DestroyQuerySuccess:
      return state.remove(action.token);
  }
  return state;
}

function tablesReducer(state = initialSelectedReportState.tables, action: Actions): SelectedReportState['tables'] {
  switch (action.type) {
    case RouterActionType.UpdateRoute:
      if (!action.payload.params.reportId) {
        return state.clear();
      }
      break;
    case ReportActionType.LoadEmbeddedReport: // fallthrough
    case ClientActionType.LoadReportSuccess:
      return state.clear();
    case ChartModelActionType.LoadEmbeddedTablesSuccess:
      return state.mergeRecords(action.payload.map((t) => tableResourceToState(action.queryToken, t)));
    case ChartModelActionType.AddTableSuccess:
      return state.mergeRecord(tableResourceToState(action.queryToken, action.payload));
    case ChartModelActionType.DestroyTableSuccess:
      return state.removeRecordByKey(action.token);
    case ChartModelActionType.UpdateTableExcludedColumns: {
      const table = state.getRecord(action.token);
      let excludeColumns = table.currentView.excludeColumns || [];
      if (excludeColumns.some((c) => c === action.columnName)) {
        excludeColumns = excludeColumns.filter((c) => c !== action.columnName);
      } else {
        excludeColumns = excludeColumns.concat(action.columnName);
      }

      return state.mergeRecord({
        ...table,
        currentView: {
          ...table.currentView,
          excludeColumns,
        },
      });
    }
    case ChartModelActionType.UpdateTableColumnSettings: {
      const table = state.getRecord(action.token);
      const columns = table.currentView.columns || {};
      return state.mergeRecord({
        ...table,
        currentView: {
          ...table.currentView,
          columns: {
            ...columns,
            [action.columnName]: action.column,
          },
        },
      });
    }
    case ChartModelActionType.UpdateTableSuccess: {
      const table = state.getRecord(action.tableResource.token);
      return state.mergeRecord({
        ...table,
        resource: action.tableResource,
      });
    }
    case TempActions.Type.DestroyQuerySuccess:
      // unrun queries don't have any tables
      const tableTokens = action.tableTokens ? action.tableTokens.toArray() : [];
      return tableTokens.length > 0 ? state.removeRecordsByKey(tableTokens) : state;
  }
  return state;
}

function reportReducer(state = initialSelectedReportState.report, action: Actions): SelectedReportState['report'] {
  switch (action.type) {
    case NotebookActionType.SaveReport:
      return action.report;
    case RouterActionType.UpdateRoute:
      if (!action.payload.params.reportId) {
        return null;
      }
      break;
    case ClientActionType.LoadReportSuccess:
      // On LoadReportSuccess, we need to handle the initial case where we actual get back
      // a new Report model with `type: Report` until the Dataset has been run once. After which
      // it will come back with the appropriate type. If we ever create a new dataset model.js model,
      // then this workaround would be able to be accounted for there.
      const typeByRoute: ReportTypes.ReportType = action.isDataset
        ? ReportTypes.ReportType.DatasetReport
        : ReportTypes.ReportType.Report;
      const reportType: ReportTypes.ReportType = action.isNew ? typeByRoute : action.reportResource.type;
      return action.reportResource.generate({
        ...action.reportResource,
        type: reportType,
      });
    case ClientActionType.LoadReportEmbeddedSpaceSuccess: {
      const newState = state.generate({
        ...state,
        _embedded: { ...state._embedded, space: action.spaceResource },
      });

      return newState;
    }
    case ReportActionType.UpdateSelectedReportSpaceSuccess: {
      const reportResource = action.payload.report;
      const newState = state.generate({
        ...state,
        space_token: action.payload.space.token,
        github_link: reportResource.github_link,
        _embedded: reportResource._embedded,
        _forms: reportResource._forms,
        _links: reportResource._links,
      });

      return newState;
    }
    case ReportActionType.UpdateReportSuccess:
      return state.generate({
        ...action.payload.report,
        _embedded: {
          ...state._embedded,
          ...action.payload.report._embedded,
        },
      });
    case ReportActionType.SetSelectedReportSharedSuccess:
      return state.generate({
        ...state,
        shared: action.payload.reportResource.shared,
        _forms: action.payload.reportResource._forms,
      });
    case ReportActionType.SetSelectedReportEmbeddedSuccess:
      return state.generate({
        ...state,
        is_embedded: action.payload.reportResource.is_embedded,
      });
  }
  return state;
}

function themeReducer(state = initialSelectedReportState.theme, action: Actions): SelectedReportState['theme'] {
  switch (action.type) {
    case ReportActionType.LoadThemeSuccess:
      return {
        name: action.name,
        token: action.token,
        href: action.href,
        customSource: action.cssSource,
      };
  }
  return state;
}

function reportIsNewReducer(
  state = initialSelectedReportState.reportIsNew,
  action: Actions
): SelectedReportState['reportIsNew'] {
  switch (action.type) {
    case ClientActionType.LoadReportSuccess:
      return action.isNew;
    case ReportRunActionType.ExecuteReportRunSuccess:
      return false;
  }
  return state;
}

function reportLayoutReducer(
  state = initialSelectedReportState.reportLayout,
  action: Actions
): SelectedReportState['reportLayout'] {
  switch (action.type) {
    case RouterActionType.UpdateRoute:
      if (!action.payload.params.reportId) {
        return '';
      }
      break;
    case ReportActionType.ReportLayoutTextChanged:
      return action.layout;
    case ClientActionType.LoadReportSuccess:
      return action.reportResource.layout;
    case ReportActionType.UpdateReportSuccess:
      return action.isNew ? action.payload.report.layout : state;
    case CellActions.Type.AddToLayoutSuccess:
      return ReportLayoutHelpers.addViz(state, { token: action.vizResource.token }, null, VizTypes.Python);
    case ChartActions.createChartSuccess.type:
      return state;
    case ReportLayoutActions.Type.AddViz:
      return ReportLayoutHelpers.addViz(
        state,
        { token: action.token },
        action.chartType,
        action.vizType,
        action.position,
        action.placement
      );
    case CellActions.Type.RemoveFromLayoutSuccess:
      return ReportLayoutHelpers.removeViz(state, 'python_' + action.vizToken);
    case ChartActions.destroyChartSuccess.type:
      return ReportLayoutHelpers.removeViz(state, 'chart_' + action.payload);
    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return ReportLayoutHelpers.removeViz(state, 'chart_' + action.token);
    case ChartModelActionType.DestroyTableSuccess:
      return ReportLayoutHelpers.removeViz(state, 'table_' + action.token);
    case ReportLayoutActions.Type.RemoveViz:
      return ReportLayoutHelpers.removeViz(state, action.vizType + '_' + action.token);
    case TempActions.Type.DestroyQuerySuccess:
      const tableTokens = action.tableTokens;
      const chartTokens = action.chartTokens;
      const tableIds = tableTokens ? tableTokens.toArray().map((t) => `table_${t}`) : [];
      const chartIds = chartTokens ? chartTokens.toArray().map((t) => `chart_${t}`) : [];
      for (const vizId of tableIds.concat(chartIds)) {
        state = ReportLayoutHelpers.removeViz(state, vizId);
      }
      return state;
    case ReportLayoutActions.Type.ToggleTextMargins:
      return ReportLayoutHelpers.toggleTextMargins(state, null, 'text_' + action.token);
    case ReportLayoutActions.Type.SetRowHeight:
      return ReportLayoutHelpers.resizeRowHeight(
        state,
        `${action.vizType}_${action.token}`,
        action.height,
        action.vizType === VizTypes.Python
      );
    case ReportLayoutActions.Type.SetFullTable:
      return ReportLayoutHelpers.setFullTable(state, null, 'table_' + action.token);
    case ReportLayoutActions.Type.SetAutoTable:
      return ReportLayoutHelpers.setAutoTable(state, null, action.token);
    case ReportLayoutActions.Type.UpdateText:
      return ReportLayoutHelpers.updateVizContent(state, 'text_' + action.token, action.text);
    case ReportLayoutActions.Type.ResizeGridElement:
      return ReportLayoutHelpers.resizeGridElement(state, action.id, null, action.direction, action.columnSize);
    case ReportLayoutActions.Type.MoveGridElement:
      return ReportLayoutHelpers.moveGridElement(state, action.id, null, action.row, action.column);
    case ReportLayoutActions.Type.CreateText:
      return ReportLayoutHelpers.addViz(state, { token: action.id }, null, 'text', action.position, null);
  }
  return state;
}

function renderedReportLayoutReducer(
  state = initialSelectedReportState.renderedReportLayout,
  action: Actions
): SelectedReportState['renderedReportLayout'] {
  // just defers to reportLayoutReducer except on ReloadFrame and ReportLayoutTextChanged
  // goal is to only change renderedReportLayout in response to programmatic updates
  switch (action.type) {
    case ReportLayoutActions.Type.ReloadFrame:
      return action.layout;
    case RouterActionType.UpdateRoute: // falls through
    case ClientActionType.LoadReportSuccess: // falls through
    case ReportActionType.UpdateReportSuccess: // falls through
    case CellActions.Type.AddToLayoutSuccess: // falls through
    case ChartActions.createChartSuccess.type: // falls through
    case ReportLayoutActions.Type.AddViz: // falls through
    case CellActions.Type.RemoveFromLayoutSuccess: // falls through
    case ChartActions.destroyChartSuccess.type: // falls through
    case ChartSwitchActions.deleteChartFromBrowserSuccess.type: // falls through
    case ChartSwitchActions.deleteChartFromTabsSuccess.type: // falls through
    case ChartModelActionType.DestroyTableSuccess: // falls through
    case ReportLayoutActions.Type.RemoveViz: // falls through
    case ReportLayoutActions.Type.ToggleTextMargins: // falls through
    case ReportLayoutActions.Type.SetRowHeight: // falls through
    case ReportLayoutActions.Type.SetFullTable: // falls through
    case ReportLayoutActions.Type.SetAutoTable: // falls through
    case ReportLayoutActions.Type.UpdateText: // falls through
    case ReportLayoutActions.Type.ResizeGridElement: // falls through
    case ReportLayoutActions.Type.MoveGridElement: // falls through
    case ReportLayoutActions.Type.CreateText:
      return reportLayoutReducer(state, action);
  }
  return state;
}

function reportDisplayNameReducer(
  state = initialSelectedReportState.reportDisplayName,
  action: Actions
): SelectedReportState['reportDisplayName'] {
  switch (action.type) {
    case RouterActionType.UpdateRoute:
      if (!action.payload.params.reportId) {
        return null;
      }
      break;
    case ClientActionType.LoadReportSuccess:
      // On LoadReportSuccess, we need to handle the initial case where we actual get back
      // a new Report model with `type: Report` until the Dataset has been run once. After which
      // it will come back with the appropriate type. If we ever create a new dataset model.js model,
      // then this workaround would be able to be accounted for there.
      const typeByRoute = action.isDataset ? ReportTypes.ReportType.DatasetReport : ReportTypes.ReportType.Report;
      const reportType = action.isNew ? typeByRoute : action.reportResource.type;
      return ReportResourceHelper.displayName(action.reportResource.name, reportType);
    case ReportActionType.UpdateReportSuccess:
      return ReportResourceHelper.displayName(action.payload.report.name, action.payload.report.type);
  }
  return state;
}

function reportRunReducer(
  state = initialSelectedReportState.reportRun,
  action: Actions
): SelectedReportState['reportRun'] {
  switch (action.type) {
    case NotebookActionType.SaveReport:
      return reportRunResourceToState(action.reportRun);
    case RouterActionType.UpdateRoute:
      if (!action.payload.params.reportId) {
        return null;
      }
      break;
    case ClientActionType.LoadReportRunSuccess: // fallthrough
    case ReportRunActionType.ExecuteReportRunSuccess:
      return reportRunResourceToState(action.reportRunResource);
    case SwitchViewActions.importedDatasetRefreshed.type:
    case SwitchViewActions.datasetAllRefreshed.type:
      return {
        ...updateReportRunLinks(state, action.shareLink),
        token: action.mostRecentReportRunToken,
      };
    case SwitchViewActions.loadImportedDatasetSuccess.type:
      return {
        ...updateReportRunLinks(state, action.shareLink),
        token: action.mostRecentReportRunToken,
      };
    case ReportRunActionType.ExecuteReportRun:
      return {
        ...state,
        running: true,
        loading: true,
        canceling: false,
        localCreatedAt: new Date(),
      };
    case ReportRunActionType.PollReportRun:
      return {
        ...state,
        loading: true,
        canceling: false,
      };
    case ReportRunActionType.PollReportRunComplete:
      return {
        ...state,
        loading: false,
        running: false,
        canceling: false,
      };
    case ReportRunActionType.PollReportRunFailed:
      return {
        ...state,
        state: 'disconnected', // Copied logic from elsewhere, don't know if it should change
        loading: false,
      };
    case ReportRunActionType.ExecuteReportRunFailed:
      return {
        ...state,
        state: 'failed',
        loading: false,
        running: false,
        errorCause: action.errorCause,
      };
    case ReportRunActionType.CancelPollReportRun:
      return {
        ...state,
        loading: false,
        running: false,
        canceling: true,
      };
    case ReportRunActionType.RefreshReportRunAfterCellExport:
      return reportRunResourceToState(action.reportRunResource);
  }

  return state;
}

function reportRunPdfExportReducer(
  state = initialSelectedReportState.reportRunPdfExport,
  action: Actions
): SelectedReportState['reportRunPdfExport'] {
  switch (action.type) {
    case RouterActionType.UpdateRoute:
      if (!action.payload.params.reportId) {
        return null;
      }
      break;
    case ClientActionType.LoadReportRunEmbeddedPdfExportSuccess:
      return action.pdfExportResource;
  }
  return state;
}

function reportSpaceReducer(
  state = initialSelectedReportState.reportSpace,
  action: Actions
): SelectedReportState['reportSpace'] {
  switch (action.type) {
    case RouterActionType.UpdateRoute:
      if (!action.payload.params.reportId) {
        return null;
      }
      break;
    case ClientActionType.LoadReportEmbeddedSpaceSuccess:
      return action.spaceResource;
    case ReportActionType.UpdateSelectedReportSpaceSuccess:
      return action.payload.space;
  }
  return state;
}

function userStarReducer(
  state = initialSelectedReportState.userStar,
  action: Actions
): SelectedReportState['userStar'] {
  switch (action.type) {
    case ClientActionType.LoadReportUserStarSuccess:
      return action.starResource;
    case ReportActionType.ToggleReportUserStarSuccess:
      return action.starResource;
  }
  return state;
}

function columnsByQueryReducer(
  state = initialSelectedReportState.columnsByQuery,
  action: Actions
): SelectedReportState['columnsByQuery'] {
  switch (action.type) {
    case ReportRunActionType.PollReportRun:
      return state.clear();
    case ReportRunActionType.ExecuteReportRun:
      if (action.runningQueryToken) {
        return state.remove(action.runningQueryToken);
      } else {
        return state.clear();
      }
    case FlamingoActions.sqlResultsSuccess.type:
      const result = Flamingo.getRecordsInline(action.request, action.result);
      // Don't reset the schema if there already is one present. In theory, rerunning the query should
      // wipe out the existing schema, allowing it to be set again here.
      if (result != null && !state.has(action.queryToken)) {
        return state.set(action.queryToken, result.columns);
      }
      break;
    case TempActions.Type.DestroyQuerySuccess:
      return state.remove(action.token);
  }
  return state;
}

function queryRunsReducer(
  state = initialSelectedReportState.queryRuns,
  action: Actions
): SelectedReportState['queryRuns'] {
  switch (action.type) {
    case ReportRunActionType.PollReportRun:
      return state.clear();
    case ReportRunActionType.ExecuteReportRun:
      if (action.runningQueryToken) {
        const run = state.getRecord(action.runningQueryToken);
        if (run) {
          return state.mergeRecord({
            ...run,
            state: 'pending',
            running: true,
            cancelQueued: false,
            runQueued: true,
            localCreatedAt: new Date(),
            content_length: null,
            bytes: null,
            resource: undefined, // old resource is no longer applicable
          });
        } else {
          // Add a placeholder query run to inform that a new query has been run
          return state.mergeRecord({
            token: '',
            datasourceToken: '',
            query_token: action.runningQueryToken,
            resource: null,
            state: 'pending',
            running: true,
            warning: false,
            cancelQueued: false,
            runQueued: true,
            timeCompleted: '',
            reportDataUrl: '',
            reportQueryUrl: '',
            reportRunUrl: '',
            errorLineHtml: '',
            errorMessage: '',
            limit: action.runningQueryLimit,
            content_length: null,
            bytes: null,
            createdAt: null,
            localCreatedAt: new Date(),
            isDbResultOversized: false,
            rawSource: null,
            renderedSource: null,
          });
        }
      }

      return state.mergeRecords(
        state.getRecords().map((r) => ({
          ...r,
          state: 'pending',
          running: true,
          cancelQueued: false,
          runQueued: true,
          localCreatedAt: new Date(),
          resource: undefined,
          content_length: null,
          bytes: null,
        }))
      );
    case ReportRunActionType.ExecuteReportRunFailed:
      return state.mergeRecord({
        ...state.getRecord(action.runningQueryToken),
        state: 'failed',
        running: false,
        cancelQueued: false,
        runQueued: false,
        localCreatedAt: state.hasRecord(action.runningQueryToken)
          ? state.getRecord(action.runningQueryToken).localCreatedAt
          : null,
      });
    case QueryActionType.BatchUpdateQueries:
      // If we have a placeholder queryRun, need to update its token
      const updatedQuery = action.payload.find((q) => state.hasRecord(q.mapping_id));

      if (updatedQuery) {
        return state
          .mergeRecord({
            ...state.getRecord(updatedQuery.mapping_id),
            query_token: updatedQuery.token,
          })
          .removeRecordByKey(updatedQuery.mapping_id);
      }
      break;
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeRecordByKey(action.token);
    case ReportRunActionType.LoadQueryRunSuccess:
      let oldRun = state.getRecord(action.resource.query_token);

      // Delete placeholder queryrun
      if (action.mapping_id) {
        oldRun = state.getRecord(action.mapping_id);
        state = state.removeRecordByKey(action.mapping_id);
      }

      let runBytes;
      let runContentLength;

      let runQueued = false;
      let cancelQueued = false;
      let localCreatedAt: Date = null;

      if (oldRun) {
        runQueued = oldRun.runQueued;
        cancelQueued = oldRun.cancelQueued;
        localCreatedAt = oldRun.localCreatedAt;

        runBytes = oldRun.bytes;
        runContentLength = oldRun.content_length;
      }

      const newRun = queryRunResourceToState(action.resource, cancelQueued, runQueued, runBytes, runContentLength);

      return state.mergeRecord({
        ...newRun,
        localCreatedAt: localCreatedAt,
      });
    case ReportRunActionType.LoadQueryRunView:
      return state.mergeRecord({
        ...state.getRecord(action.resource.query_token),
        content_length: action.resultResource.content_length,
        bytes: action.resultResource.bytes,
      });
    case ReportRunActionType.LoadQueryResultSuccess: // fallthrough
    case ReportRunActionType.LoadQueryResultFailure:
      return state.mergeRecord({
        ...state.getRecord(action.queryRun.query_token),
        running: false,
        cancelQueued: false,
        runQueued: false,
        localCreatedAt: state.hasRecord(action.queryRun.query_token)
          ? state.getRecord(action.queryRun.query_token).localCreatedAt
          : null,
      });
    case ReportRunActionType.CancelQueryRun:
      if (state.hasRecord(action.token)) {
        // Optimistic update of cancel state
        return state.mergeRecord({
          ...state.getRecord(action.token),
          state: 'cancelled',
          running: false,
          cancelQueued: true,
          runQueued: false,
        });
      }
      break;
  }

  return state;
}

function pollingReportRunReducer(
  state = initialSelectedReportState.pollingReportRun,
  action: Actions
): SelectedReportState['pollingReportRun'] {
  switch (action.type) {
    case ReportRunActionType.CancelPollReportRun: // fallthrough
    case ReportRunActionType.ExecuteReportRunFailed:
      return false;
    case ReportRunActionType.PollReportRunComplete: // fallthrough
    case ReportRunActionType.PollReportRunFailed:
      return false;
    case ClientActionType.LoadReportRunSuccess: // fallthrough
    case ReportRunActionType.PollReportRun: // fallthrough
    case ReportRunActionType.ExecuteReportRun:
      return true;
  }

  return state;
}

function chartModelsReducer(
  state = initialSelectedReportState.chartModels,
  action: Actions
): SelectedReportState['chartModels'] {
  switch (action.type) {
    case ReportRunActionType.PollReportRun:
      return state.clear();
    case ReportRunActionType.ExecuteReportRun:
      if (action.runningQueryToken) {
        return state.removeRecordByKey(action.runningQueryToken);
      }

      break;
    //return state.clear();
    case ReportRunActionType.LoadQueryRunSuccess:
      return state.removeRecordByKey(action.resource.token);
    case ReportRunActionType.LoadQueryResultSuccess: {
      return state.mergeRecords([
        queryResultToState(
          action.queryResource,
          action.resource,
          action.queryRun,
          action.model,
          action.dataset && action.dataset.content
        ),
      ]);
    }
    case ReportRunActionType.ReportRoutesNoScriptOrLegacy:
    case ReportRunActionType.LoadQueryResultFailure: {
      return state.mergeRecord(makeErrorQueryResult(action.queryResource, action.queryRun));
    }
  }

  return state;
}

function queryViewsReducer(
  state = initialSelectedReportState.queryViewResourceByQuery,
  action: Actions
): SelectedReportState['queryViewResourceByQuery'] {
  switch (action.type) {
    case ReportRunActionType.LoadQueryRunView: {
      return {
        ...state,
        [action.resource.query_token]: action.resource.embedded('view'),
      };
    }
  }

  return state;
}

function switchAttrsReducer(
  state = initialSelectedReportState.switchAttrsByQuery,
  action: Actions
): SelectedReportState['switchAttrsByQuery'] {
  switch (action.type) {
    case ReportRunActionType.LoadQueryResultFailure: {
      return immRemoveKey(state, action.queryRun.query_token);
    }
    case ReportRunActionType.LoadQueryRunView: {
      const calcAttrs = QueryRunResourceHelpers.makeCalcAttrs(action.resource, action.resultResource);
      const columnAttrs = QueryRunResourceHelpers.makeColumnAttrs(action.resource, action.resultResource);
      const attrResources = QueryRunResourceHelpers.unwrapQueryRunAttrs(action.resource);

      const queryToken = action.resource.query_token;
      const queryRunToken = action.resource.token;

      const resourceMap = {};
      for (const resource of attrResources) {
        resourceMap[resource.name] = resource;
      }

      return {
        ...state,
        [queryToken]: {
          name: queryToken,
          from: queryRunToken,
          columns: columnAttrs,
          calcs: calcAttrs,
          resources: resourceMap,
        },
      };
    }
  }
  return state;
}

// deprecated, use switchAttrsReducer instead
function modelReducer(state = initialSelectedReportState.model, action: Actions): SelectedReportState['model'] {
  switch (action.type) {
    case ReportRunActionType.LoadQueryResultFailure: {
      return Switch.Views.remove(state, action.queryRun.query_token);
    }
    case ReportRunActionType.LoadQueryRunView: {
      const { view } = action;
      return Switch.Views.merge(state, view);
    }
    default:
      return state;
  }
}

function calcEditorModalReducer(
  state = initialSelectedReportState.calcEditorModal,
  action: Actions
): SelectedReportState['calcEditorModal'] {
  switch (action.type) {
    case SwitchViewActions.openCalcEditor.type: {
      const {
        viewName,
        switchViewToken,
        calc,
        duplicate,
        viewMode,
        readOnly,
        isImportedDataset,
        canShowFieldDescription,
      } = action;
      return {
        ...state,
        isOpen: true,
        viewName,
        switchViewToken,
        calc,
        duplicate,
        viewMode,
        readOnly,
        isImportedDataset,
        canShowFieldDescription,
      };
    }
    case ChartDesignerActionType.OpenCalcEditor: {
      return {
        ...state,
        ...action,
        isOpen: true,
      };
    }
    case ChartDesignerActionType.CloseCalcEditor: {
      return {
        isOpen: false,
        viewName: null,
      };
    }
    case SwitchAttrsActions.createSwitchViewAttr.type:
    case SwitchAttrsActions.updateSwitchViewAttr.type: {
      const { attr: calc } = action;
      return { ...state, calc };
    }
  }

  return state;
}

function hasRecentRunReducer(
  state = initialSelectedReportState.hasRecentReportRun,
  action: Actions
): SelectedReportState['hasRecentReportRun'] {
  if (action.type === ReportActionType.UpdateRecentReport) {
    return action.payload.hasRecentReportRun;
  }

  return state;
}

function recentReportRunReducer(
  state = initialSelectedReportState.recentReportRun,
  action: Actions
): SelectedReportState['recentReportRun'] {
  if (action.type === ReportActionType.UpdateRecentReportDetails) {
    return action.payload.recentReportRun;
  }

  return state;
}

export const SelectedReportReducer: ReducerTree<AppState, SelectedReportState, Actions> = {
  model: modelReducer,
  chartModels: chartModelsReducer,
  chartsByQuery: chartsByQueryReducer,
  columnsByQuery: columnsByQueryReducer,
  creator: creatorReducer,
  notebookViz: normalizedNotebookVizReducer,
  pollingReportRun: pollingReportRunReducer,
  queries: queriesReducer,
  queryRuns: queryRunsReducer,
  queryViewResourceByQuery: queryViewsReducer,
  report: reportReducer,
  theme: themeReducer,
  reportIsNew: reportIsNewReducer,
  reportLayout: reportLayoutReducer,
  renderedReportLayout: renderedReportLayoutReducer,
  reportDisplayName: reportDisplayNameReducer,
  reportRun: reportRunReducer,
  reportRunPdfExport: reportRunPdfExportReducer,
  reportSpace: reportSpaceReducer,
  switchAttrsByQuery: switchAttrsReducer,
  tables: tablesReducer,
  tablesByQuery: tablesByQueryReducer,
  userStar: userStarReducer,
  calcEditorModal: calcEditorModalReducer,
  hasRecentReportRun: hasRecentRunReducer,
  recentReportRun: recentReportRunReducer,
};
